import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Heart, MessageCircle, Share2, Repeat2, ExternalLink, Users, ImageIcon, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";

/**
 * Public per-user wall page. No auth required. Perfect for
 * TikTok / Instagram / X bio links (e.g. stardomfans.com/u/dj-soul).
 * Only surfaces `visibility="community"` posts. Personal posts stay private.
 */
const PublicWall = () => {
  const { identifier } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/u/${encodeURIComponent(identifier)}?limit=30`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This user doesn't exist yet." : "Failed to load this wall.");
      } finally {
        setLoading(false);
      }
    })();
  }, [identifier]);

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => toast.success("Wall link copied — share it anywhere!"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <p className="text-[#8A92A0] text-sm">Loading wall...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-[#050810] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Wall not found</h1>
        <p className="text-[#8A92A0] mb-6">{error}</p>
        <Link to="/" className="px-6 py-2.5 rounded-full bg-[#EC008C] text-black font-semibold text-sm" data-testid="wall-home-link">
          Back to StardomFans
        </Link>
      </div>
    );
  }
  const p = data.profile;
  const artist = p.artist;
  const posts = data.posts || [];
  const counts = data.counts || {};

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="public-wall-page">
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/70 backdrop-blur-lg border-b border-[#1A2030]">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#8A92A0] hover:text-white text-sm" data-testid="wall-back-link">
            <ArrowLeft className="w-4 h-4" /> StardomFans
          </Link>
          <span className="text-xs text-[#8A92A0] hidden sm:inline">Public wall</span>
        </div>
      </header>

      <main className="pt-20 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Profile card */}
          <div className="card p-6 mb-8" data-testid="wall-profile-card">
            <div className="flex items-start gap-5 flex-wrap">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#EC008C]/40 to-[#EC008C]/10 flex items-center justify-center overflow-hidden flex-shrink-0 border-2 border-[#EC008C]/30">
                {p.picture ? (
                  <img src={p.picture} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-['Unbounded'] text-3xl font-black text-[#EC008C]">
                    {(p.name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white mb-1 break-words" data-testid="wall-name">
                  {artist?.stage_name || p.name}
                </h1>
                {artist?.stage_name && p.name && artist.stage_name !== p.name && (
                  <p className="text-sm text-[#8A92A0] mb-2">{p.name}</p>
                )}
                {artist?.bio && (
                  <p className="text-sm text-[#C0C6D0] mb-3 leading-relaxed">{artist.bio}</p>
                )}
                <div className="flex flex-wrap items-center gap-4 text-xs text-[#8A92A0]">
                  <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5 text-[#EC008C]" /> {counts.public_posts || 0} posts</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#EC008C]" /> {counts.followers || 0} followers</span>
                  <span className="flex items-center gap-1 text-[#8DB87A]"><ShieldCheck className="w-3.5 h-3.5" /> Public wall</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                {artist && (
                  <Link to={`/artist/${artist.artist_id}`} className="px-4 py-2 rounded-full bg-[#EC008C] text-black text-xs font-semibold hover:bg-[#FF3399] transition-colors text-center" data-testid="wall-follow-btn">
                    Follow & Support
                  </Link>
                )}
                <Button onClick={copyLink} variant="outline" className="border-[#1A2030] text-white hover:border-[#EC008C] rounded-full text-xs" data-testid="wall-share-btn">
                  <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share Wall
                </Button>
              </div>
            </div>
          </div>

          {/* Posts */}
          {posts.length === 0 ? (
            <div className="card p-12 text-center" data-testid="wall-empty">
              <MessageCircle className="w-10 h-10 text-[#8A92A0] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No public posts yet</h3>
              <p className="text-[#8A92A0] text-sm">Nothing shared to the Community feed from this account.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="wall-posts">
              {posts.map((post) => (
                <article key={post.post_id} className="card p-5" data-testid={`wall-post-${post.post_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#1A2030] flex items-center justify-center overflow-hidden">
                      {post.user_picture ? <img src={post.user_picture} alt={post.user_name} className="w-full h-full object-cover" /> : <span className="text-xs text-white">{(post.user_name || "?").charAt(0)}</span>}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-semibold">{post.user_name}</p>
                      <p className="text-xs text-[#4A5260]">{new Date(post.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  {post.text && (
                    <p className="text-sm text-white whitespace-pre-wrap break-words leading-relaxed mb-3">{post.text}</p>
                  )}
                  {post.image_url && (
                    <img src={post.image_url} alt="Post" className="rounded-xl max-h-96 object-cover w-full mb-3" />
                  )}
                  {post.video_url && (
                    <video src={post.video_url} controls className="rounded-xl w-full mb-3" />
                  )}
                  {post.link_url && (
                    <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#EC008C] hover:underline">
                      <ExternalLink className="w-3 h-3" /> {post.link_url}
                    </a>
                  )}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#1A2030] text-xs text-[#8A92A0]">
                    <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {post.likes || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {post.comment_count || 0}</span>
                    <span className="flex items-center gap-1"><Repeat2 className="w-3.5 h-3.5" /> {post.repost_count || 0}</span>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Footer CTA */}
          <div className="text-center mt-12">
            <Link to="/" className="text-sm text-[#8A92A0] hover:text-[#EC008C]" data-testid="wall-footer-cta">
              Powered by <span className="text-white font-semibold">StardomFans.com</span> · Build your own free stage
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicWall;
